import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

export const USAContext = createContext();
const API_URL = import.meta.env.VITE_API_URL;

export const USAProvider = ({ children }) => {
  const { shipmentId } = useParams(); // Use useParams to get the shipmentId from the URL

  const [boxes, setBoxes] = useState([]);
  const [shipmentInfo, setShipmentInfo] = useState(null);
  const [shipmentItems, setShipmentItems] = useState([]);
  const [asinData, setAsinData] = useState([]);
  const [clientsData, setClientsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null); // New state variable
  const [activeStep, setActiveStep] = useState(0);
  const [trackingCodeMap, setTrackingCodeMap] = useState({}); // New state for tracking codes

  const fetchShipmentData = useCallback(async () => {
    setLoading(true);
    try {
      const [shipmentResponse, itemsResponse] = await Promise.all([
        fetch(`https://app.yyzprep.ca/api/shipments/outbound/${shipmentId}`, {
          headers: {
            'Authorization': 'Bearer Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq',
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }).then(response => response.json()),
        fetch(`https://app.yyzprep.ca/api/shipments/outbound/${shipmentId}/outbound-shipment-item`, {
          headers: {
            'Authorization': 'Bearer Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq',
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }).then(response => response.json())
      ]);

      setShipmentInfo(shipmentResponse.shipment);
      setShipmentItems(itemsResponse.items);
      await getPlans();  // Add this line


      const asinList = itemsResponse.items.map(item => item.item.asin).filter(asin => asin);

      // Refresh prices automatically with 6-hour caching
      if (asinList.length > 0) {
        try {
          console.log(`Auto-refreshing prices for ${asinList.length} ASINs...`);
          const priceRefreshResponse = await fetch(`${API_URL}/api/refresh-prices`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ 
              asins: asinList,
              employee: 'Auto-Refresh' 
            })
          });
          const priceRefreshResult = await priceRefreshResponse.json();
          console.log('Price refresh completed:', priceRefreshResult.summary);
        } catch (error) {
          console.error('Price refresh failed (non-critical):', error);
        }
      }

      const [asinDataResponse, clientsDataResponse] = await Promise.all([
        fetch(`${API_URL}/api/get_asin_data`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ asins: asinList })
        }).then(response => response.json()),
        fetch(`${API_URL}/api/get_clients`)
          .then(response => response.json())
      ]);

      setAsinData(asinDataResponse);
      setClientsData(clientsDataResponse);

      const boxData = await fetchBoxes();
      setBoxes(boxData);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  }, [shipmentId]);

  const reloadAsinData = async () => {
    try {
      const asinList = shipmentItems.map(item => item.item.asin);
  
      const response = await fetch(`${API_URL}/api/get_asin_data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ asins: asinList })
      });
  
      if (!response.ok) {
        throw new Error('Failed to fetch ASIN data');
      }
  
      const data = await response.json();
      setAsinData(data);
      console.log('ASIN data reloaded successfully:', data);
    } catch (error) {
      console.error('Error fetching ASIN data:', error);
    }
  };


  const getPlans = async () => {
    try {
      const response = await fetch(`https://app.yyzprep.ca/api/fba-transport/v2024/plans?api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq&outbound_shipment_id=${shipmentId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch plans');
      }
      const data = await response.json();
      
      setPlans(data.data.filter(plan => 
        !plan.is_canceled && 
        plan.packing_options.some(option => option.confirmed_at !== null)
      ));
      console.log('Fetched plans:', data.data.filter(plan => 
        !plan.is_canceled && 
        plan.packing_options.some(option => option.confirmed_at !== null)
      ));
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };


  const fetchBoxes = useCallback(async (planToFetch = null) => {
    try {
      // Use provided plan or fall back to selectedPlan
      const planToUse = planToFetch || selectedPlan;
      
      // Check if we have a plan to use
      if (!planToUse || !planToUse.id) {
        console.log('No plan provided for box fetch, selectedPlan:', !!selectedPlan, 'planToFetch:', !!planToFetch);
        return [];
      }
      
      console.log('Fetching boxes for plan ID:', planToUse.id);
      
      // First try the traditional plan-based endpoint
      let endpoint = `${API_URL}/api/get_box_contents/${planToUse.id}`;
      let response = await fetch(endpoint);
      let data = await response.json();
      
      // If no boxes found with plan ID, try the outbound-based endpoint
      if (!Array.isArray(data) || data.length === 0) {
        console.log('No boxes found for plan ID, trying outbound endpoint with shipmentId:', shipmentId);
        endpoint = `${API_URL}/api/get_all_box_content_plans/${shipmentId}`;
        response = await fetch(endpoint);
        data = await response.json();
      }
      
      if (Array.isArray(data)) {
        const groupedData = groupAndSortByBoxNumber(data);
        console.log("Fetched boxes:", groupedData.length, "boxes from", endpoint);
        setBoxes(groupedData);
        return groupedData;
      } else {
        console.error('dbResponse is not an array:', data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching boxes:', error);
      return [];
    }
  }, [selectedPlan, shipmentId]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  // New function to fetch detailed plan info and extract tracking codes
  const fetchPlanDetails = async (planId) => {
    try {
      const response = await fetch(`https://app.yyzprep.ca/api/fba-transport/v2024/plans/${planId}?api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`);
      if (!response.ok) {
        throw new Error('Failed to fetch plan details');
      }
      const data = await response.json();
      
      // Extract tracking codes and map them to box numbers
      const trackingMap = extractTrackingCodes(data.data);
      setTrackingCodeMap(trackingMap);
      console.log('Tracking code map:', trackingMap);
      
      return data.data;
    } catch (error) {
      console.error('Error fetching plan details:', error);
      return null;
    }
  };

  // Function to extract tracking codes from plan data
  const extractTrackingCodes = (planData) => {
    const trackingMap = {};
    
    if (!planData.placement_options) return trackingMap;
    
    // Find the selected placement option
    const selectedPlacementOption = planData.placement_options.find(option => option.is_selected);
    
    if (!selectedPlacementOption || !selectedPlacementOption.shipments) return trackingMap;
    
    // Process each shipment
    selectedPlacementOption.shipments.forEach(shipment => {
      if (shipment.tracking_details && shipment.tracking_details.spd_tracking_detail) {
        const spdTrackingItems = shipment.tracking_details.spd_tracking_detail.spd_tracking_items;
        
        // Create mapping from amazon_box_id to tracking_id
        const boxIdToTrackingId = {};
        spdTrackingItems.forEach(item => {
          boxIdToTrackingId[item.box_id] = item.tracking_id;
        });
        
        // Map to internal box numbers using the boxes array
        if (shipment.boxes) {
          shipment.boxes.forEach(box => {
            const trackingId = boxIdToTrackingId[box.amazon_box_id];
            if (trackingId) {
              trackingMap[box.box_number] = trackingId;
            }
          });
        }
      }
    });
    
    return trackingMap;
  };

  // Function to get tracking code for a specific box number
  const getTrackingCodeForBox = (boxNumber) => {
    return trackingCodeMap[boxNumber] || null;
  };

  useEffect(() => {
    fetchShipmentData();
  }, [fetchShipmentData]);

  // Create a refreshBoxes function that can work with or without a plan
  const refreshBoxes = useCallback(async (planToRefresh = null) => {
    // If no plan provided, try to use the current selectedPlan or try outbound endpoint directly
    if (!planToRefresh && !selectedPlan) {
      console.log('No plan available, trying to fetch boxes using outbound endpoint directly');
      try {
        const endpoint = `${API_URL}/api/get_all_box_content_plans/${shipmentId}`;
        const response = await fetch(endpoint);
        const data = await response.json();
        
        if (Array.isArray(data)) {
          const groupedData = groupAndSortByBoxNumber(data);
          console.log("Refreshed boxes via outbound endpoint:", groupedData.length, "boxes");
          setBoxes(groupedData);
          return groupedData;
        }
        return [];
      } catch (error) {
        console.error('Error refreshing boxes via outbound endpoint:', error);
        return [];
      }
    }
    
    // Otherwise use the regular fetchBoxes function
    return await fetchBoxes(planToRefresh);
  }, [fetchBoxes, selectedPlan, shipmentId]);

  return (
    <USAContext.Provider value={{ 
      shipmentId,
      boxes, 
      shipmentInfo, 
      shipmentItems, 
      asinData, 
      reloadAsinData,
      setBoxes,
      clientsData, 
      loading,
      plans,
      selectedPlan,
      setSelectedPlan,
      refreshBoxes,
      activeStep,
      handleNext,
      handleBack,
      handleReset,
      trackingCodeMap,
      fetchPlanDetails,
      getTrackingCodeForBox,
    }}>
      {children}
    </USAContext.Provider>
  );
};

const groupAndSortByBoxNumber = (data) => {
  const groupedData = data.reduce((acc, item) => {
    const boxNumber = item.BOX_NUMBER;
    if (!acc[boxNumber]) {
      acc[boxNumber] = {
        length: item.LENGTH,
        width: item.WIDTH,
        height: item.HEIGHT,
        weight: item.WEIGHT,
        items: []
      };
    }
    
    // Only push non-empty items into the items array
    if (item.FNSKU || item.SKU || item.ASIN || item.QTY || item.TITLE) {
      acc[boxNumber].items.push({
        asin: item.ASIN,
        fnsku: item.FNSKU,
        sku: item.SKU,
        quantity: item.QTY || 0,
        title: item.TITLE
      });
    }

    return acc;
  }, {});

  const sortedData = Object.keys(groupedData)
    .sort((a, b) => Number(a) - Number(b))
    .map(boxNumber => ({
      box_number: parseInt(boxNumber), // Change to box_number to match expected structure
      length: groupedData[boxNumber].length,
      width: groupedData[boxNumber].width,
      height: groupedData[boxNumber].height,
      weight: groupedData[boxNumber].weight,
      items: groupedData[boxNumber].items
    }));

  return sortedData;
};
